import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getHistory, clearHistory, SessionHistoryItem } from '../utils/sessionHistory';
import { preferencesApi, UserPreferences } from '../services/api';
import BackButton from '../components/BackButton';
import Button from '../components/Button';
import PageTransition from '../components/PageTransition';
import PreferencesEditor from '../components/PreferencesEditor';
import ContactModal from '../components/ContactModal';

const EMPTY_PREFS: Omit<UserPreferences, 'isConfigured'> = {
  likedCuisines: [], dislikedCuisines: [], likedMenuTags: [], dislikedMenuTags: [],
};

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, requestAuth, logout, updateProfile, updatePreferencesConfigured } = useAuth();
  const [history, setHistory] = useState<SessionHistoryItem[]>([]);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  // Preferences
  const [prefs, setPrefs] = useState<Omit<UserPreferences, 'isConfigured'>>(EMPTY_PREFS);
  const [showPrefsEditor, setShowPrefsEditor] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    setHistory(getHistory());
  }, []);

  useEffect(() => {
    if (isAuthenticated && !prefsLoaded) {
      preferencesApi.get().then((data) => {
        const { isConfigured: _, ...rest } = data;
        setPrefs(rest);
        setPrefsLoaded(true);
      }).catch(() => {});
    }
  }, [isAuthenticated, prefsLoaded]);

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  const handleClear = () => {
    clearHistory();
    setHistory([]);
  };

  const handleLogin = async () => {
    await requestAuth();
  };

  const handleSavePrefs = async () => {
    setPrefsSaving(true);
    try {
      await preferencesApi.update(prefs);
      updatePreferencesConfigured(true);
      setShowPrefsEditor(false);
    } catch {/* ignore */} finally {
      setPrefsSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateProfile({ name: name.trim() });
      setEditingName(false);
    } catch {
      // ошибка уже обработана в api interceptor
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <BackButton onClick={() => navigate('/')} label="" />
          <h1 className="text-lg font-bold text-gray-900">Профиль</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* User card */}
        {isAuthenticated && user ? (
          <div className="bg-white rounded-xl border border-gray-100 p-5">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-red-400 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xl font-bold">
                  {(user.name || user.email || '?')[0].toUpperCase()}
                </span>
              </div>

              <div className="flex-1 min-w-0">
                {editingName ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ваше имя"
                      autoFocus
                      maxLength={100}
                      className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                    />
                    <Button
                      variant="primary"
                      size="xs"
                      disabled={saving}
                      onClick={handleSaveName}
                    >
                      {saving ? '...' : 'OK'}
                    </Button>
                    <button
                      onClick={() => { setEditingName(false); setName(user.name || ''); }}
                      className="text-xs text-gray-400 hover:text-gray-600"
                    >
                      Отмена
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-base font-semibold text-gray-900">
                        {user.name || 'Без имени'}
                      </p>
                      <button
                        onClick={() => setEditingName(true)}
                        className="text-orange-500 hover:text-orange-600 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{user.email || user.phone}</p>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={logout}
              className="mt-4 w-full py-2 rounded-lg border border-gray-200 text-sm text-gray-500 hover:text-red-500 hover:border-red-200 transition-all"
            >
              Выйти
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-orange-50 mx-auto mb-3 flex items-center justify-center">
              <svg className="w-7 h-7 text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <h2 className="text-base font-bold text-gray-900 mb-1">Войдите в аккаунт</h2>
            <p className="text-sm text-gray-500 mb-4">
              Для сохранения истории, избранного и предпочтений
            </p>
            <Button size="lg" fullWidth onClick={handleLogin}>
              Войти по email
            </Button>
          </div>
        )}

        {/* Preferences */}
        {isAuthenticated && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-orange-500" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
                <h2 className="text-sm font-semibold text-gray-700">Мои предпочтения</h2>
              </div>
              <button
                onClick={() => setShowPrefsEditor(!showPrefsEditor)}
                className="text-xs text-orange-500 hover:text-orange-600 font-medium transition-colors"
              >
                {showPrefsEditor ? 'Свернуть' : 'Изменить'}
              </button>
            </div>

            {!showPrefsEditor && (
              <div className="px-5 pb-4">
                {prefs.likedCuisines.length === 0 && prefs.likedMenuTags.length === 0 ? (
                  <p className="text-xs text-gray-400">Предпочтения не заданы</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {[...prefs.likedCuisines, ...prefs.likedMenuTags].map((t) => (
                      <span key={t} className="px-2.5 py-1 bg-orange-50 text-orange-600 rounded-full text-xs font-medium">{t}</span>
                    ))}
                    {[...prefs.dislikedCuisines, ...prefs.dislikedMenuTags].map((t) => (
                      <span key={t} className="px-2.5 py-1 bg-gray-100 text-gray-400 rounded-full text-xs line-through">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showPrefsEditor && (
              <div className="px-5 pb-5 border-t border-gray-50">
                <div className="pt-4">
                  <PreferencesEditor value={prefs} onChange={setPrefs} />
                </div>
                <div className="flex gap-2 mt-5">
                  <Button variant="primary" size="sm" onClick={handleSavePrefs} disabled={prefsSaving}>
                    {prefsSaving ? 'Сохраняем...' : 'Сохранить'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowPrefsEditor(false)}>
                    Отмена
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Support */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <button
            onClick={() => setShowContact(true)}
            className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
          >
            <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">Написать в поддержку</p>
              <p className="text-xs text-gray-400">Вопрос, проблема или предложение</p>
            </div>
            <svg className="w-4 h-4 text-gray-300 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* Session history */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700">История сессий</h2>
            {history.length > 0 && (
              <button
                onClick={handleClear}
                className="text-xs text-gray-400 hover:text-red-500 transition-colors"
              >
                Очистить
              </button>
            )}
          </div>

          {history.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-2">
                <svg className="w-6 h-6 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 2h18v6H3z" /><path d="M3 10h18v6H3z" /><path d="M3 18h18v4H3z" />
                </svg>
              </div>
              <p className="text-sm text-gray-400">Пока нет сессий</p>
              <p className="text-xs text-gray-300 mt-1">Создайте первую сессию из каталога</p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((s) => {
                const date = new Date(s.date);
                const dateStr = date.toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'long',
                });
                const timeStr = date.toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit',
                });

                return (
                  <button
                    key={s.sessionId}
                    onClick={() => navigate(`/session/${s.sessionId}/results`)}
                    className="w-full bg-white rounded-xl border border-gray-100 hover:border-orange-200 p-4 flex items-center gap-4 transition-colors text-left group"
                  >
                    {/* Date circle */}
                    <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-100 transition-colors">
                      <svg className="w-4.5 h-4.5 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{dateStr}</p>
                      <p className="text-xs text-gray-400">{timeStr}</p>
                    </div>

                    {/* Match count */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {s.matchCount > 0 ? (
                        <>
                          <span className="text-sm font-semibold text-orange-600">{s.matchCount}</span>
                          <svg className="w-4 h-4 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                          </svg>
                        </>
                      ) : (
                        <span className="text-xs text-gray-300">нет совпадений</span>
                      )}
                    </div>

                    {/* Arrow */}
                    <svg className="w-4 h-4 text-gray-300 group-hover:text-orange-400 transition-colors flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
    </PageTransition>
  );
};

export default ProfilePage;
